import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserService } from '../services/userService';
import { REFRESH_TOKEN, TOKEN, USER_ROLES } from '../common/constants/localStorage';
import { getToken, updateToken } from '../services/tokenService';
import { useAuthService } from '../services/authService';
import { routesPath } from '../common/constants/routesPath';
import { isTwoFactorPending } from '../utils/twoFactorSession';
import { isImpersonationTab } from '../services/impersonationSession';
import { stopImpersonation } from '../services/impersonationService';
import { setAnalyticsUser } from '../services/analytics';
// import globalNotification from '../common/hooks/globalNotification';
// import { useTeamService } from '../services/teamService';

export const AuthContext = createContext(null);

// a token kept in state and in the storage tokenService picks (sessionStorage in an impersonation tab)
const useStoredToken = tokenName => {
    const [storedToken, setStoredToken] = useState(() => {
        try {
            return getToken(tokenName);
        } catch (err) {
            return null;
        }
    });

    const setToken = newToken => {
        updateToken(tokenName, newToken);
        setStoredToken(newToken);
    };

    return [storedToken, setToken];
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useStoredToken(TOKEN);
    const [refreshToken, setRefreshToken] = useStoredToken(REFRESH_TOKEN);
    const [currentUser, setCurrentUser] = useState();
    const [isUserLoading, setIsUserLoading] = useState(true);

    const navigate = useNavigate();
    const userService = useUserService();
    const authService = useAuthService();

    const routeSignIn = `${routesPath.AUTH.PATH}/${routesPath.AUTH.SIGN_IN}`;
    // const { updateMembership, findUserMembership } = useTeamService();

    const fetchUser = useCallback(async () => {
        const isImpersonating = isImpersonationTab();
        let hasFailed = false;

        try {
            // a 2FA user's password-only token is refused by /auth-consumers/me until the TOTP step is done;
            // asking would 401, fail the refresh (there is no refresh token yet) and end the sign-in.
            // An impersonation token carries the target's isTwoFactorEnable but is never password-only.
            if (token && (isImpersonating || !isTwoFactorPending(token))) {
                setIsUserLoading(true);
                const { data } = await userService.getUser();
                setCurrentUser(data);
                // GA knows the consumer by id only, never by email (SC-QA-43)
                setAnalyticsUser(data.id);
                updateToken(USER_ROLES, data.userRoleNames);
            }
        } catch (e) {
            hasFailed = true;
            // an impersonation tab never signs out: a 401 has already ended it in the axios interceptor, and a
            // 503 keeps the session for a retry
            if (!isImpersonating) {
                logout();
            }
            console.error(e);
        } finally {
            // without a user the pages cannot render; the impersonation bar (Stop) stays usable above the loader
            if (!(isImpersonating && hasFailed)) {
                setIsUserLoading(false);
            }
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [token]);

    // merges a server-confirmed change into the loaded user without reloading it (no AppLoader, no logout path);
    // functional, so a late answer never overwrites a newer user, and a signed-out state stays signed out
    const patchCurrentUser = useCallback(patch => {
        setCurrentUser(prev => prev && { ...prev, ...patch });
    }, []);

    const setAuth = (token, refreshToken) => {
        setToken(token);
        setRefreshToken(refreshToken);
    };

    const logout = async () => {
        // in an impersonation tab "log out" means stop impersonating; the consumer's own session is not touched
        if (isImpersonationTab()) {
            await stopImpersonation();
            return;
        }

        try {
            if (!currentUser) {
                throw new Error();
            }

            // const { data } = await findUserMembership(currentUser.id);

            // if (!data) {
            //     throw new Error();
            // }

            // await updateMembership({ id: data.id, isActive: false });
        } catch (e) {
            console.log(e);
        } finally {
            authService.logout();
            setAnalyticsUser(null);
            updateToken(TOKEN, null);
            updateToken(REFRESH_TOKEN, null);
            updateToken(USER_ROLES, null);
            navigate(routeSignIn);
        }
    };

    // const changeTeam = async teamId => {
    //     try {
    //         const { data } = await authService.changeTeam(teamId);

    //         setAuth(data.accessToken, data.refreshToken);
    //     } catch (error) {
    //         globalNotification.open({ message: 'This membership is not valid' });
    //     } finally {
    //         await fetchUser();
    //     }
    // };

    return (
        <AuthContext.Provider
            value={{
                token,
                setAuth,
                logout,
                fetchUser,
                currentUser,
                patchCurrentUser,
                isUserLoading,
                // changeTeam,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
